import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Divider,
  Modal,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { dateFormatter } from '../../../utils/dateFormatter';
import { useEffect, useMemo, useState } from 'react';
import {
  addBinToRepairOrderRequest,
  changeStatusOfFinalizationRequest,
  closeRepairOrderRequest,
  createSegregationFinalizeRequest,
  createSegregationResultRequest,
  createTransferRequest,
  loadRepairOrderByIdRequest,
} from '../../../redux/repairOrdersRedux';
import { getUser } from '../../../redux/usersRedux';
import { loadBinsByWarehouseRequest } from '../../../redux/binsRedux';
import { io } from 'socket.io-client';
import { SOCKET_URL } from '../../../config';

const socket = io(SOCKET_URL);

export function summarizeFinalizations(segregation = [], binsLookup = {}) {
  const byCatBin = {};
  const catFieldMap = {
    GOOD: 'good',
    DAMAGED: 'damaged',
    TO_REPAIR: 'toRepair',
  };

  segregation.forEach((seg) => {
    (seg.result || []).forEach((r) => {
      // 1) Zbierz "pierwotny" rozkład po binach per kategoria na podstawie initialQuantity (fallback: quantity)
      const perCatInitialTotals = { GOOD: 0, DAMAGED: 0, TO_REPAIR: 0 };
      const perCatInitialBinFromR = {
        GOOD: new Map(),
        DAMAGED: new Map(),
        TO_REPAIR: new Map(),
      };

      (r.finalization || []).forEach((f) => {
        const cat = f.category; // "GOOD" | "DAMAGED" | "TO_REPAIR"
        const binId = f.binId;
        const init = Number(f.initialQuantity ?? f.currentQuantity) || 0; // << KLUCZOWE
        if (!cat || !binId || init <= 0) return;

        perCatInitialTotals[cat] += init;
        perCatInitialBinFromR[cat].set(
          binId,
          (perCatInitialBinFromR[cat].get(binId) || 0) + init,
        );
      });

      // 2) Agreguj BIEŻĄCE finalizacje (currentQty + finalizations) z f.quantity
      (r.finalization || []).forEach((f) => {
        const cat = f.category;
        const binId = f.binId;
        const qty = Number(f.currentQuantity) || 0; // bieżąca ilość
        const id = f.id;
        const status = f.status; // "OPEN" | "CLOSE"
        if (!cat || !binId || qty < 0) return;

        if (!byCatBin[cat]) byCatBin[cat] = {};
        if (!byCatBin[cat][binId]) {
          byCatBin[cat][binId] = {
            initialQty: 0,
            currentQty: 0,
            finalizations: new Map(), // id -> { id, currentQty, statuses:Set }
          };
        }

        const bucket = byCatBin[cat][binId];
        bucket.currentQty += qty;

        if (id != null) {
          if (!bucket.finalizations.has(id)) {
            bucket.finalizations.set(id, {
              id,
              currentQty: 0,
              statuses: new Set(),
            });
          }
          const fin = bucket.finalizations.get(id);
          fin.currentQty += qty;
          if (status != null) fin.statuses.add(status);
        }
      });

      // 3) Rozdziel INITIAL na podstawie initialQuantity (NIE bieżącego quantity)
      ['GOOD', 'DAMAGED', 'TO_REPAIR'].forEach((cat) => {
        const totalInitForCat = perCatInitialTotals[cat];
        const binsFromR = perCatInitialBinFromR[cat]; // Map<binId, initQtyFromR>

        if (totalInitForCat > 0 && binsFromR?.size) {
          for (const [binId, initQtyFromR] of binsFromR.entries()) {
            if (!byCatBin[cat]) byCatBin[cat] = {};
            if (!byCatBin[cat][binId]) {
              byCatBin[cat][binId] = {
                initialQty: 0,
                currentQty: 0,
                finalizations: new Map(),
              };
            }
            // tu nie ma proporcji – sumujemy dokładnie to, co "pierwotnie" wpadło do tego bina
            byCatBin[cat][binId].initialQty += Number(initQtyFromR) || 0;
          }
        }
      });
    });
  });

  // 4) rows
  const rows = Object.entries(byCatBin).flatMap(([category, binsObj]) =>
    Object.entries(binsObj).map(([binId, data]) => {
      const finalizations = Array.from(data.finalizations.values()).map(
        (x) => ({
          id: x.id,
          currentQty: x.currentQty,
          status: x.statuses.has('OPEN')
            ? 'OPEN'
            : x.statuses.size
            ? 'CLOSE'
            : null,
        }),
      );

      return {
        category,
        binId,
        binName: binsLookup[binId] || binId,
        initialQty: data.initialQty, // stabilne – z initialQuantity
        currentQty: data.currentQty, // bieżące – z quantity
        finalizations,
      };
    }),
  );

  rows.sort(
    (a, b) =>
      a.category.localeCompare(b.category) ||
      a.binName.localeCompare(b.binName),
  );

  return rows;
}

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '50%',
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: 4,
};

const categories = [
  {
    id: 'GOOD',
    name: 'Wyrób zgodny',
  },
  {
    id: 'DAMAGED',
    name: 'Wyrób uszkodzony',
  },
  {
    id: 'TO_REPAIR',
    name: 'Wyrób do naprawy',
  },
];

const RepairOrderDetails = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [repairOrder, setRepairOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const user = useSelector((state) => getUser(state));
  const [bins, setBins] = useState([]);
  const [sourceBinId, setSourceBinId] = useState('');
  const [targetBinId, setTargetBinId] = useState('');
  const [currentBinQuantity, setCurrentBinQuantity] = useState(0);
  const [casBinQuantity, setCasBinQuantity] = useState(0);
  const [quantity, setQuantity] = useState(0);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [tooltipMessage, setTooltipMessage] = useState('');
  const [targetBinName, setTargetBinName] = useState('');
  const [finalizations, setFinalizations] = useState([]);
  const [modalResult, setModalResult] = useState(false);
  const [modalTransfer, setModalTransfer] = useState(false);
  const [resultBinId, setResultBinId] = useState('');
  const [resultBinName, setResultBinName] = useState('');
  const [category, setCategory] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [resultBinQuantity, setResultBinQuantity] = useState(0);
  const [resultCasQuantity, setResultCasQuantity] = useState(0);
  const [categoryQuantityArr, setCategoryQuantityArr] = useState([]);
  const [segregationId, setSegregationId] = useState('');
  const [message, setMessage] = useState('');
  const [success, setSuccess] = useState(false);
  const [actionType, setActionType] = useState(''); // NEW_REPAIR | REPAIR_AGAIN
  const [finalizationId, setFinalizationId] = useState('');
  const [sourceId, setSourceId] = useState('');

  useEffect(() => {
    const start = async () => {
      const repairOrderData = await dispatch(loadRepairOrderByIdRequest(id));
      const bins = await dispatch(
        loadBinsByWarehouseRequest(user.users.warehouse),
      );
      if (repairOrderData) {
        setRepairOrder(repairOrderData.data);
        setBins(bins.data);
        setLoading(false);
      } else {
        navigate('/repair-orders');
      }
    };
    start();
  }, [dispatch, id]);

  useEffect(() => {
    // Połączenie WebSocket i nasłuchiwanie zdarzenia "createSegregation"
    socket.on('segregation', async (segregation) => {
      console.log('Otrzymano nowe segregowanie:', segregation);
      const repairOrderData = await dispatch(loadRepairOrderByIdRequest(id));
      setRepairOrder(repairOrderData.data);
      // Aktualizacja stanu komponentu
    });

    socket.on('segregationFinalization', async (finalization) => {
      console.log('Otrzymano nowe zakończenie segregacji:', finalization);
      const repairOrderData = await dispatch(loadRepairOrderByIdRequest(id));
      setRepairOrder(repairOrderData.data);
      // Aktualizacja stanu komponentu
    });

    return () => {
      socket.off('segregation'); // Wyczyść po odmontowaniu
      socket.off('segregationFinalization'); // Wyczyść po odmontowaniu
    };
  }, [dispatch]);

  const handleChangeStatusName = (status) => {
    switch (status) {
      case 'OPEN':
        return 'Nowy';
      case 'IN_PROGRESS':
        return 'W trakcie';
      case 'FINALIZED':
        return 'Zamknięte';
      default:
        return 'Nieznany status';
    }
  };

  const totals = useMemo(() => {
    if (!repairOrder || !repairOrder.segregation)
      return { good: 0, damaged: 0, toRepair: 0 };
    return repairOrder.segregation.reduce(
      (acc, seg) => {
        if (Array.isArray(seg.result)) {
          seg.result.forEach((r) => {
            acc.good += Number(r?.good) || 0;
            acc.damaged += Number(r?.damaged) || 0;
            acc.toRepair += Number(r?.toRepair) || 0;
          });
        }
        return acc;
      },
      { good: 0, damaged: 0, toRepair: 0 },
    );
  }, [repairOrder]);

  const rows = useMemo(() => {
    if (!repairOrder || !repairOrder.segregation) return [];
    const binsLookup = bins.reduce((acc, bin) => {
      acc[bin.id] = bin.binName;
      return acc;
    }, {});
    return summarizeFinalizations(repairOrder.segregation, binsLookup);
  }, [repairOrder, bins]);

  // sumy kategorii (stopka)
  const totals_2 = useMemo(() => {
    return rows.reduce(
      (acc, r) => {
        acc[r.category] = (acc[r.category] || 0) + r.quantity;
        acc.ALL += r.quantity;
        return acc;
      },
      { GOOD: 0, DAMAGED: 0, TO_REPAIR: 0, ALL: 0 },
    );
  }, [rows]);

  const handleCategoryName = (category) => {
    switch (category) {
      case 'GOOD':
        return 'Wyrób zgodny';
      case 'DAMAGED':
        return 'Wyrób uszkodzony';
      case 'TO_REPAIR':
        return 'Wyrób do naprawy';
      default:
        return 'Nieznana kategoria';
    }
  };

  const handleStatusName = (status) => {
    switch (status) {
      case 'IN_PROGRESS':
        return 'W trakcie segregacji';
      case 'FINALIZED':
        return 'Segregacja zakończona';
      case 'CANCELED':
        return 'Segregacja anulowana';
      default:
        return 'Nieznany status';
    }
  };

  const handleCloseTransferModal = () => {
    setActionType('');
    setModalTransfer(false);
  };

  const handleActionTransfer = async () => {
    const quantityToTransfer =
      parseInt(currentBinQuantity) * repairOrder.product.palletQty +
      parseInt(casBinQuantity);
    setQuantity(quantityToTransfer);
    console.log(quantityToTransfer);
    const data = {
      id: sourceId,
      sourceBinId: sourceBinId,
      targetBinId: targetBinId,
      line: targetBinName,
      quantity: quantityToTransfer,
      repairOrderId: repairOrder.id,
      createdById: user.users.id,
    };

    if (actionType === 'REPAIR_AGAIN') {
      const newData = {
        id: repairOrder.id,
        binId: sourceBinId,
        quantity: quantityToTransfer,
      };

      const res_again = await dispatch(addBinToRepairOrderRequest(newData));

      if (res_again.status === 201) {
        let remainingQuantity = quantityToTransfer;

        for (const f of finalizationId) {
          if (remainingQuantity <= 0) break;

          if (f.status === 'OPEN') {
            if (remainingQuantity >= f.quantity) {
              // całkowite rozliczenie finalizacji
              await dispatch(
                changeStatusOfFinalizationRequest({
                  id: f.id,
                  quantity: 0,
                  status: 'CLOSED',
                }),
              );
              remainingQuantity -= f.quantity;
            } else {
              // częściowe rozliczenie finalizacji
              const newQty = f.quantity - remainingQuantity;
              await dispatch(
                changeStatusOfFinalizationRequest({
                  id: f.id,
                  quantity: newQty,
                  status: 'OPEN',
                }),
              );
              remainingQuantity = 0;
            }
          }
        }

        setModalTransfer(false);
        setCurrentBinQuantity(0);
        setCasBinQuantity(0);
        setSourceBinId('');
        setTargetBinId('');
        setError(false);
        setTargetBinName('');
        setTooltipMessage('');
        setMessage(
          'Przekazanie do segregacji zostało pomyślnie zaktualizowane.',
        );
        setSuccess(true);
        setErrorMessage('');
        return;
      }
    }

    const res = await dispatch(createTransferRequest(data));
    if (res.status === 201) {
      setModalTransfer(false);
      setCurrentBinQuantity(0);
      setCasBinQuantity(0);
      setSourceBinId('');
      setTargetBinId('');
      setError(false);
      setTargetBinName('');
      setTooltipMessage('');
      setMessage('Przekazanie do segregacji zostało pomyślnie zaktualizowane.');
      setSuccess(true);
      setErrorMessage('');
      setQuantity(0);
    } else {
      setError(true);
      setErrorMessage(res.data.message || 'Błąd podczas przekazywania');
    }
    console.log('Transfer response:', res);
  };

  const handleCloseResultModal = () => {
    setModalResult(false);
  };

  const handleResult = () => {
    const quantity =
      parseInt(resultBinQuantity) * repairOrder.product.palletQty +
      parseInt(resultCasQuantity);
    if (categoryQuantityArr.some((el) => el.id === categoryId)) {
      const existingCategory = categoryQuantityArr.find(
        (el) => el.id === categoryId,
      );
      existingCategory.quantity += quantity;
      setCategoryQuantityArr([...categoryQuantityArr]);
    } else {
      const newCategoryQuantity = {
        id: categoryId,
        quantity: quantity,
      };
      setCategoryQuantityArr((prev) => [...prev, newCategoryQuantity]);
    }

    const binData = {
      binId: resultBinId,
      category: categoryId,
      currentQuantity: quantity,
      initialQuantity: quantity,
    };

    if (
      finalizations.some(
        (el) => el.binId === resultBinId && el.category === categoryId,
      )
    ) {
      const existingFinalization = finalizations.find(
        (el) => el.binId === resultBinId && el.category === categoryId,
      );
      existingFinalization.currentQuantity += quantity;
      existingFinalization.initialQuantity += quantity;
      setFinalizations([...finalizations]);
    } else {
      setFinalizations((prev) => [...prev, binData]);
    }
    console.log('Result Bin Data:', binData, finalizations);
  };

  const handleSubmitResult = async () => {
    const dataResult = {
      segregationId: segregationId,
      good: categoryQuantityArr.find((el) => el.id === 'GOOD')?.quantity || 0,
      damaged:
        categoryQuantityArr.find((el) => el.id === 'DAMAGED')?.quantity || 0,
      toRepair:
        categoryQuantityArr.find((el) => el.id === 'TO_REPAIR')?.quantity || 0,
      userId: user.users.id,
    };
    const resultRes = await dispatch(
      createSegregationResultRequest(dataResult),
    );

    if (resultRes.status === 201) {
      const data = {
        resultId: resultRes.data.id,
        userId: user.users.id,
        finalizations: finalizations,
      };
      const finalizeRes = await dispatch(
        createSegregationFinalizeRequest(data),
      );
      if (finalizeRes.status === 201) {
        setSegregationId('');
        setFinalizations([]);
        setCategoryQuantityArr([]);
        setResultBinId('');
        setResultBinName('');
        setResultBinQuantity(0);
        setResultCasQuantity(0);
        setCategoryId('');
        setCategory('');

        setSuccess(true);
        setMessage('Wynik segregacji został pomyślnie zaktualizowany.');
        setModalResult(false);
      } else {
        setError(true);
        setErrorMessage(finalizeRes.data.message || 'Błąd podczas finalizacji');
      }
    } else {
      setError(true);
      setErrorMessage(resultRes.data.message || 'Błąd podczas przekazywania');
    }
  };
  console.log('Repair Order Details:', repairOrder);
  console.log('Rows:', rows);
  console.log(finalizationId);
  if (!loading) {
    return (
      <Box>
        <Snackbar
          open={error}
          autoHideDuration={6000}
          onClose={() => setError(false)}
          message={errorMessage}
        >
          <Alert severity="error" variant="filled">
            {errorMessage}
          </Alert>
        </Snackbar>
        <Snackbar
          open={success}
          autoHideDuration={6000}
          onClose={() => setSuccess(false)}
          message={message}
        >
          <Alert severity="success" variant="filled">
            {message}
          </Alert>
        </Snackbar>
        <Typography variant="h5" gutterBottom sx={{ fontStyle: 'italic' }}>
          Zlecenie naprawy
        </Typography>
        <Divider sx={{ width: '100%', mt: 1, mb: 3, bgcolor: '#C09A53' }} />

        <Typography
          variant="h6"
          component="h2"
          gutterBottom
          sx={{ fontWeight: 'bold' }}
        >
          Dane podstawowe:
        </Typography>
        <Typography variant="body1" gutterBottom>
          {'Data utworzenia zlecenia'}:{' '}
          <Typography
            component="span"
            sx={{ fontWeight: 'bold', fontStyle: 'italic' }}
          >
            {dateFormatter(repairOrder.createdAt)}
          </Typography>
        </Typography>
        <Typography variant="body1" gutterBottom>
          {'Uzytkownik'}:{' '}
          <Typography
            component="span"
            sx={{ fontWeight: 'bold', fontStyle: 'italic' }}
          >
            {`${repairOrder.createdBy.firstName} ${repairOrder.createdBy.lastName}`}
          </Typography>
        </Typography>
        <Typography variant="body1" gutterBottom>
          {'Status'}:{' '}
          <Typography
            component="span"
            sx={{ fontWeight: 'bold', fontStyle: 'italic' }}
          >
            {handleChangeStatusName(repairOrder.status)}
          </Typography>
        </Typography>
        <Divider sx={{ width: '100%', mt: 1, mb: 3, bgcolor: '#C09A53' }} />
        <Typography
          sx={{
            fontWeight: 'bold',
          }}
          variant="h6"
          component="h2"
          gutterBottom
        >
          {'Szczegóły zlecenia:'}{' '}
        </Typography>
        <Typography variant="body1" gutterBottom>
          {'Numer materiału'}:{' '}
          <Typography
            component="span"
            sx={{ fontWeight: 'bold', fontStyle: 'italic' }}
          >
            {repairOrder.product.materialNumber}
          </Typography>
        </Typography>
        <Typography variant="body1" gutterBottom>
          {'Nazwa materiału'}:{' '}
          <Typography
            component="span"
            sx={{ fontWeight: 'bold', fontStyle: 'italic' }}
          >
            {repairOrder.product.description}
          </Typography>
        </Typography>
        <Typography variant="body1" gutterBottom>
          {'Partia'}:{' '}
          <Typography
            component="span"
            sx={{ fontWeight: 'bold', fontStyle: 'italic' }}
          >
            {repairOrder.batch}
          </Typography>
        </Typography>
        <Typography variant="body1" gutterBottom>
          {'Ilość początkowa'}:{' '}
          <Typography
            component="span"
            sx={{ fontWeight: 'bold', fontStyle: 'italic' }}
          >
            {repairOrder.totalPallets.toLocaleString('pl-PL')} cas
          </Typography>
        </Typography>
        <Typography variant="body1" gutterBottom>
          {'Ilość pozostała'}:{' '}
          <Typography
            component="span"
            sx={{ fontWeight: 'bold', fontStyle: 'italic' }}
          >
            {repairOrder.sourceBins
              .reduce((sum, bin) => sum + bin.currentQuantity, 0)
              .toLocaleString('pl-PL')}{' '}
            cas
          </Typography>
        </Typography>
        {repairOrder.sourceBins.reduce(
          (sum, bin) => sum + bin.currentQuantity,
          0,
        ) === 0 &&
          repairOrder.segregation.length > 0 &&
          repairOrder.segregation.every((seg) => seg.status === 'FINALIZED') &&
          repairOrder.status !== 'FINALIZED' && (
            <Button
              variant="contained"
              onClick={() => {
                // handleClose();
                dispatch(closeRepairOrderRequest(repairOrder.id));
              }}
            >
              Zamknij zlecenie
            </Button>
          )}
        <Divider sx={{ width: '100%', mt: 1, mb: 3, bgcolor: '#C09A53' }} />
        <Typography
          sx={{
            fontWeight: 'bold',
          }}
          variant="h6"
          component="h2"
          gutterBottom
        >
          Skrócone podsumowanie segregacji:
        </Typography>
        <Typography variant="body1" gutterBottom>
          Wyrób zgodny:{' '}
          <span style={{ fontWeight: 'bold' }}>{totals.good} cas</span>
        </Typography>
        <Typography variant="body1" gutterBottom>
          Wyrób do naprawy:{' '}
          <span style={{ fontWeight: 'bold' }}>{totals.toRepair} cas</span>
        </Typography>
        <Typography variant="body1" gutterBottom>
          Wyrób do utylizacji:{' '}
          <span style={{ fontWeight: 'bold' }}>{totals.damaged} cas</span>
        </Typography>

        <Divider sx={{ width: '100%', mt: 1, mb: 3, bgcolor: '#C09A53' }} />
        <Typography
          sx={{
            fontWeight: 'bold',
          }}
          variant="h6"
          component="h2"
          gutterBottom
        >
          {'Lokalizacja piwa do segregacji:'}{' '}
        </Typography>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Lokalizacja</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                Ilość początkowa [cas]
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                Ilość pozostała [cas]
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>% Realizacji</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Akcja</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {repairOrder.sourceBins.map((bin) => (
              <TableRow key={bin.id}>
                <TableCell>{bin.bin.binName}</TableCell>
                <TableCell>
                  {bin.initialQuantity.toLocaleString('pl-PL')} cas
                </TableCell>
                <TableCell>
                  {bin.currentQuantity.toLocaleString('pl-PL')} cas
                </TableCell>
                <TableCell>
                  {(
                    100 -
                    (bin.currentQuantity / bin.initialQuantity) * 100
                  ).toFixed(2)}{' '}
                  %
                </TableCell>
                <TableCell>
                  {bin.currentQuantity > 0 && (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        setSourceBinId(bin.bin.id);
                        setTooltipMessage(
                          `Maksymalna ilość to: ${
                            (bin.currentQuantity -
                              (bin.currentQuantity %
                                repairOrder.product.palletQty)) /
                            repairOrder.product.palletQty
                          } palet + ${
                            bin.currentQuantity % repairOrder.product.palletQty
                          } cas`,
                        );
                        setModalTransfer(true);
                        setActionType('');
                        setSourceId(bin.id);
                      }}
                    >
                      Przekaż do segregacji
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Divider sx={{ width: '100%', mt: 1, mb: 3, bgcolor: '#C09A53' }} />
        <Typography
          sx={{
            fontWeight: 'bold',
          }}
          variant="h6"
          component="h2"
          gutterBottom
        >
          W trakcie segregacji:
        </Typography>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Lokalizacja</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                Ilość pozostała [cas]
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                Data przekazania
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Akcja</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {repairOrder.segregation.map((transfer) => (
              <TableRow key={transfer.id}>
                <TableCell>{transfer.targetBin.binName}</TableCell>
                <TableCell>
                  {transfer.quantity.toLocaleString('pl-PL')} cas
                </TableCell>
                <TableCell>{dateFormatter(transfer.createdAt)}</TableCell>
                <TableCell>{handleStatusName(transfer.status)}</TableCell>

                <TableCell>
                  {transfer.status !== 'FINALIZED' && (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        setModalResult(true);
                        setSegregationId(transfer.id);
                        setTooltipMessage(
                          `Maksymalna ilość to: ${
                            (transfer.quantity -
                              (transfer.quantity %
                                repairOrder.product.palletQty)) /
                            repairOrder.product.palletQty
                          } palet + ${
                            transfer.quantity % repairOrder.product.palletQty
                          } cas`,
                        );
                      }}
                    >
                      Dodaj działanie
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Divider sx={{ width: '100%', mt: 1, mb: 3, bgcolor: '#C09A53' }} />
        <Typography
          sx={{
            fontWeight: 'bold',
          }}
          variant="h6"
          component="h2"
          gutterBottom
        >
          Podsumowanie segregacji:
        </Typography>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                Lokalizacja
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                Ilość początkowa [cas]
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                Ilość pozostała [cas]
              </TableCell>

              <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                Status wyrobu gotowego
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Akcja</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows
              .sort((a, b) => a.binName.localeCompare(b.binName))
              .map((row) => {
                const openFinalizations =
                  row.finalizations
                    ?.filter((f) => f.status === 'OPEN')
                    .map((f) => ({
                      id: f.id,
                      quantity: f.currentQty,
                      status: f.status,
                    })) ?? [];

                const canShowButton =
                  row.currentQty > 0 &&
                  row.category === 'TO_REPAIR' &&
                  openFinalizations.length > 0;

                return (
                  <TableRow key={row.binId + row.category}>
                    <TableCell sx={{ textAlign: 'center' }}>
                      {row.binName}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      {row.initialQty.toLocaleString('pl-PL')} cas
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      {row.category === 'TO_REPAIR'
                        ? row.currentQty.toLocaleString('pl-PL') + ' cas'
                        : ''}
                    </TableCell>
                    <TableCell
                      sx={{
                        textTransform: 'capitalize',
                        color:
                          row.category === 'GOOD'
                            ? 'green'
                            : row.category === 'DAMAGED'
                            ? 'red'
                            : 'orange',
                        fontWeight: 'bold',
                        fontStyle: 'italic',
                        fontSize: '1.1rem',
                        textAlign: 'center',
                      }}
                    >
                      {handleCategoryName(row.category)}
                    </TableCell>

                    <TableCell>
                      {canShowButton && (
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => {
                            setSourceBinId(row.binId);
                            setTooltipMessage(
                              `Maksymalna ilość to: ${
                                (row.currentQty -
                                  (row.currentQty %
                                    repairOrder.product.palletQty)) /
                                repairOrder.product.palletQty
                              } palet + ${
                                row.currentQty % repairOrder.product.palletQty
                              } cas`,
                            );
                            setModalTransfer(true);
                            setActionType('REPAIR_AGAIN');
                            setFinalizationId(openFinalizations);
                          }}
                        >
                          Przekaż do naprawy
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                Razem:
              </TableCell>
              <TableCell sx={{ textAlign: 'center' }}>
                {rows
                  .reduce((acc, row) => acc + row.initialQty, 0)
                  .toLocaleString('pl-PL')}{' '}
                cas
              </TableCell>
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>
        <Divider sx={{ width: '100%', mt: 1, mb: 3, bgcolor: '#C09A53' }} />
        <Modal open={modalTransfer} onClose={handleCloseTransferModal}>
          <Box sx={style}>
            <Typography variant="h6" component="h2" gutterBottom sx={{ mb: 2 }}>
              Przekaż wyrób do segregacji
            </Typography>
            <Divider sx={{ width: '100%', mt: 1, mb: 3, bgcolor: '#C09A53' }} />
            <Box>
              {actionType !== 'REPAIR_AGAIN' && (
                <Autocomplete
                  disablePortal
                  id="combo-source"
                  sx={{ width: { xs: '50%', md: '20%' }, mb: 2, mx: 1 }}
                  options={bins.map((el) => ({
                    id: el.id,
                    label: `${el.binName}`,
                  }))}
                  value={targetBinName}
                  onChange={(e, value) => {
                    setTargetBinId(value ? value.id : '');
                    setTargetBinName(value ? value.label : '');
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label={'Lokalizacja docelowa'} />
                  )}
                />
              )}
              <Tooltip
                placement="top"
                title={<Typography>{tooltipMessage}</Typography>}
                style={{ fontSize: '20px' }}
              >
                <TextField
                  label="Ilość palet"
                  sx={{ width: { xs: '100%', md: '30%' }, mb: 2, mx: 1 }}
                  value={currentBinQuantity}
                  onChange={(e) => setCurrentBinQuantity(e.target.value)}
                />

                <TextField
                  label="Ilość końcowki"
                  sx={{ width: { xs: '100%', md: '30%' }, mb: 2, mx: 1 }}
                  value={casBinQuantity}
                  onChange={(e) => setCasBinQuantity(e.target.value)}
                />
              </Tooltip>
            </Box>
            <Button
              variant="contained"
              color="primary"
              sx={{ mt: 1, mx: 1 }}
              onClick={() => {
                handleActionTransfer();
              }}
            >
              Przekaż
            </Button>
          </Box>
        </Modal>
        <Modal open={modalResult} onClose={handleCloseResultModal}>
          <Box sx={style}>
            <Typography variant="h6" component="h2" gutterBottom sx={{ mb: 2 }}>
              Wprowadź wynik segregacji
            </Typography>
            <Divider sx={{ width: '100%', mt: 1, mb: 3, bgcolor: '#C09A53' }} />
            <Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', mb: 2 }}>
                <Autocomplete
                  disablePortal
                  id="combo-source"
                  sx={{ width: { xs: '50%', md: '30%' }, mb: 2, mx: 1 }}
                  options={bins.map((el) => ({
                    id: el.id,
                    label: `${el.binName}`,
                  }))}
                  value={resultBinName}
                  onChange={(e, value) => {
                    setResultBinId(value ? value.id : '');
                    setResultBinName(value ? value.label : '');
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label={'Lokalizacja docelowa'} />
                  )}
                />
                <Autocomplete
                  disablePortal
                  id="combo-source"
                  sx={{ width: { xs: '50%', md: '30%' }, mb: 2, mx: 1 }}
                  options={categories.map((el) => ({
                    id: el.id,
                    label: `${el.name}`,
                  }))}
                  value={category}
                  onChange={(e, value) => {
                    setCategoryId(value ? value.id : '');
                    setCategory(value ? value.label : '');
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label={'Rodzaj wyrobu gotowego'} />
                  )}
                />
              </Box>
              <Tooltip
                placement="top"
                title={<Typography>{tooltipMessage}</Typography>}
                style={{ fontSize: '20px' }}
              >
                <TextField
                  label="Ilość palet"
                  sx={{ width: { xs: '100%', md: '30%' }, mb: 2, mx: 1 }}
                  value={resultBinQuantity}
                  onChange={(e) => setResultBinQuantity(e.target.value)}
                />

                <TextField
                  label="Ilość końcowki"
                  sx={{ width: { xs: '100%', md: '30%' }, mb: 2, mx: 1 }}
                  value={resultCasQuantity}
                  onChange={(e) => setResultCasQuantity(e.target.value)}
                />
              </Tooltip>
              <Button
                variant="contained"
                color="primary"
                sx={{ mt: 1, mx: 1 }}
                onClick={() => {
                  handleResult();
                }}
              >
                Zapisz wynik
              </Button>
            </Box>
            <Divider sx={{ width: '100%', mt: 1, mb: 3, bgcolor: '#C09A53' }} />
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Lokalizacja</TableCell>
                  <TableCell>Rodzaj wyrobu gotowego</TableCell>
                  <TableCell>Ilość palet</TableCell>
                  <TableCell>Ilość końcówki</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {finalizations.length > 0 ? (
                  finalizations.map((el) => (
                    <TableRow key={el.binId}>
                      <TableCell>
                        {bins.find((bin) => bin.id === el.binId)?.binName}
                      </TableCell>
                      <TableCell>{handleCategoryName(el.category)}</TableCell>
                      <TableCell>
                        {Math.floor(
                          el.currentQuantity / repairOrder.product.palletQty,
                        )}
                      </TableCell>
                      <TableCell>
                        {el.currentQuantity % repairOrder.product.palletQty}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      <Typography
                        sx={{ fontWeight: 'bold' }}
                        variant="body2"
                        color="black"
                      >
                        Brak dodanych lokalizacji
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <Divider sx={{ width: '100%', mt: 1, mb: 3, bgcolor: '#C09A53' }} />
            <Button
              variant="contained"
              color="primary"
              sx={{ mt: 1, mx: 1 }}
              onClick={() => {
                handleSubmitResult();
              }}
            >
              Zatwierdź segregację
            </Button>
          </Box>
        </Modal>
      </Box>
    );
  }
};

export default RepairOrderDetails;
